import { memo, useCallback, useContext, useMemo } from 'react'
import { CustomizeMoneyFlowContext, MoneyFlowContext } from '../../context/MoneyFlowContext.tsx'
import { useCurrentEntity } from '../../hooks/useCurrentEntity.ts'
import { useDiagramConfig } from '../../hooks/useDiagramConfig.ts'
import { hexToRgb } from './diagram/color.ts'
import { Link } from './diagram/diagram.ts'
import { DiagramLinkLabel, LinkLabelData } from './DiagramLinkLabel.tsx'
import { DiagramLinkLabelCompact } from './DiagramLinkLabelCompact.tsx'
import { DiagramViewPortContext } from './DiagramViewPortContext.tsx'
import { getPointOnPath } from './helpers/svg.ts'
import { InteractionWrapHandle } from './InteractionWrap.tsx'
import { UserLabels } from "../../backend/types/userLabels.ts"
import { Edge } from "../../backend/types/edge.ts"
import { TransactionTableTabs } from '../TransactionTable/transactionTable.tsx'

type DiagramLinksLabelsProps = {
    interactionWrap: InteractionWrapHandle | null,
    labels: UserLabels['tags']
}
export const DiagramLinksLabels = memo(({
    interactionWrap,
    labels
}: DiagramLinksLabelsProps) => {
    const {
        graph,
        linkProps,
        transactionTable: { onChangeTab }
    } = useContext(MoneyFlowContext)
    const { setCurrentEntity } = useCurrentEntity()
    const {
        config: diagramConfig
    } = useDiagramConfig()
    const { currentEntity } = useCurrentEntity()
    const {
        highlightLink,
        removeEffects,
    } = useContext(CustomizeMoneyFlowContext)
    const { viewPort } = useContext(DiagramViewPortContext)

    const maxWidth = diagramConfig.linkLabelText.maxWidth
    const minWidth = diagramConfig.linkLabelText.minWidth

    let width = viewPort.width / 25

    if (width < minWidth) {
        width = minWidth
    } else if (width > maxWidth) {
        width = maxWidth
    }
    const height = width / 2

    const linksLabelsData = useMemo(() => {
        const labelsData: Record<string, LinkLabelData> = {}
        for (const link of graph.links) {
            labelsData[link.id] = {
                value: link.value,
                position: getPointOnPath(link.path!,  0.5)
            }
        }
        return labelsData
    }, [graph.links])

    const overlappingLabels: Record<string, LinkLabelData> = {}

    for (let i = 0; i < graph.links.length; i++) {
        const firstLabelId = graph.links[i].id
        const firstLabelData = linksLabelsData[firstLabelId]

        if (!firstLabelData) {
            continue
        }

        if (firstLabelId in overlappingLabels) continue

        for (let j = i; j < graph.links.length; j++) {
            const secondLabelId = graph.links[j].id
            const secondLabelData = linksLabelsData[secondLabelId]

            if (!secondLabelData) {
                continue
            }

            if (secondLabelId in overlappingLabels) continue

            if (firstLabelId !== secondLabelId &&
                (firstLabelData.position.x - width / 2 < secondLabelData.position.x + width / 2) &&
                (firstLabelData.position.x + width / 2 > secondLabelData.position.x - width / 2) &&
                (firstLabelData.position.y - height / 2 < secondLabelData.position.y + height / 2) &&
                (firstLabelData.position.y + height / 2 > secondLabelData.position.y - height / 2)) {
                overlappingLabels[firstLabelId] = firstLabelData
                overlappingLabels[secondLabelId] = secondLabelData
            }
        }
    }

    const maxValue = Math.max(...graph.links.map((l) => l.value))
    const minValue = Math.min(...graph.links.map((l) => l.value))

    const onMouseOver = useCallback((link: Link) => highlightLink(link), [highlightLink])
    const onMouseLeave = useCallback(() => removeEffects(), [removeEffects])
    const onClick = useCallback(async (link: Link) => {
        if (!interactionWrap?.isPanning()) {
            setCurrentEntity(link.id)
            onChangeTab(TransactionTableTabs.ALL)
        }
    }, [interactionWrap, onChangeTab, setCurrentEntity])

    const labelsComponents = []

    for (const node of graph.nodes) {
        for (let idx = 0; idx < node.targetLinks.length; idx++) {
            const link = node.targetLinks[idx]

            const id = link.id
            const valueProportion = (link.value - minValue) / (maxValue - minValue)
            const colorFrom = hexToRgb(diagramConfig.linkLabelText.fromColor)
            const colorTo = hexToRgb(diagramConfig.linkLabelText.toColor)
            const opacity = linkProps[link.id]?.opacity || 1
            const selected = (currentEntity as Edge)?._id === link.semanticData._id

            labelsComponents.push(!(id in overlappingLabels) ?
                <DiagramLinkLabel
                    key={id}
                    link={link}
                    path={link.path!}
                    amount={link.value}
                    id={id}
                    height={Math.min(width, 20)}
                    width={Math.min(width, 20)}
                    x={linksLabelsData[id].position.x}
                    y={linksLabelsData[id].position.y}
                    onClick={onClick}
                    onMouseOver={onMouseOver}
                    onMouseLeave={onMouseLeave}
                    labelOffset={(idx - Math.floor(node.targetLinks.length / 2)) / node.targetLinks.length}
                    labels={labels[link.id] || []}
                    backgroundColor={selected ? diagramConfig.selected.color : 'white'}
                    strokeColor={selected ? 'transparent' : undefined}
                    filter={selected ? undefined : 'drop-shadow( 0px 1px 2px rgba(0, 0, 0, .3))'}
                    opacity={selected ? 1 : opacity}
                    valueProportion={valueProportion}
                    colorFrom={colorFrom}
                    colorTo={colorTo}
                /> : <DiagramLinkLabelCompact
                    key={id}
                    link={link}
                    x={overlappingLabels[id].position.x}
                    y={overlappingLabels[id].position.y}
                    valueProportion={valueProportion}
                    id={id}
                    height={width / 5}
                    width={width / 5}
                    onClick={onClick}
                    onMouseOver={onMouseOver}
                    onMouseLeave={onMouseLeave}
                    opacity={opacity}
                    colorFrom={colorFrom}
                    colorTo={colorTo}
                />)
        }
    }

    return labelsComponents
})
